// One connected Amazon marketplace.
//
// A separate card from Shopify's because almost nothing lines up: there
// is no webhook to register (Amazon does not push — Portal polls), the
// credentials are an LWA triple rather than a token pair, and the
// warehouse is chosen at creation because an unrouted Amazon channel
// rejects every order it receives and a rejected order costs seller
// metrics.
//
// Card order follows the setup sequence: credentials → catalogue →
// orders. Each step needs the one before it.

import { useCallback, useEffect, useState } from "react";
import {
    Loader2, Wifi, WifiOff, RefreshCw, Trash2, AlertTriangle, DownloadCloud,
} from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../../../i18n";
import { apiFetch } from "../../../api/apiFetch";
import { useReadOnly } from "../../../access/ReadOnly";
import SecretField from "./SecretField";
import VendorSubAccountRow from "./VendorSubAccountRow";
import SkuMapPanel from "./SkuMapPanel";
import type { AmazonTestResult, ChannelPatch, SalesChannelConfig } from "./types";

interface Props {
    channel: SalesChannelConfig;
    onUpdated: (next: SalesChannelConfig) => void;
    onDeleted: (channelCode: string) => void;
}

const fmtAbsolute = (iso: string | null | undefined): string => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const AmazonChannelCard = ({ channel, onUpdated, onDeleted }: Props) => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const base = `/admin/integrations/${encodeURIComponent(channel.channelCode)}`;

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [warehouses, setWarehouses] = useState<{ warehouse: string; country: string | null }[]>([]);

    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState<AmazonTestResult | null>(null);

    const [syncing, setSyncing] = useState(false);
    const [polling, setPolling] = useState(false);
    const [skuReload, setSkuReload] = useState(0);

    const [clientIdDraft, setClientIdDraft] = useState(channel.amazonLwaClientId);
    const [displayNameDraft, setDisplayNameDraft] = useState(channel.displayName);
    useEffect(() => { setClientIdDraft(channel.amazonLwaClientId); }, [channel.id, channel.amazonLwaClientId]);
    useEffect(() => { setDisplayNameDraft(channel.displayName); }, [channel.id, channel.displayName]);

    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await apiFetch("/settings", {});
                if (!res.ok) return;
                const rows = await res.json();
                if (cancelled) return;
                setWarehouses((Array.isArray(rows) ? rows : []).map((r) => ({
                    warehouse: r.warehouse, country: r.country ?? null,
                })));
            } catch { /* silent */ }
        })();
        return () => { cancelled = true; };
    }, []);

    const patch = useCallback(async (body: ChannelPatch): Promise<void> => {
        if (readOnly) return;
        setSaving(true);
        setError(null);
        try {
            const res = await apiFetch(base, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(json?.message ?? `HTTP ${res.status}`);
            if (json?.channel) onUpdated(json.channel);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
            throw err;
        } finally {
            setSaving(false);
        }
    }, [base, onUpdated, readOnly]);

    const toggle = (field: keyof ChannelPatch, value: boolean) => {
        patch({ [field]: value } as ChannelPatch).catch(() => {});
    };

    const runTest = async () => {
        if (readOnly) return;
        setTesting(true);
        setTestResult(null);
        try {
            const res = await apiFetch(`${base}/amazon/test`, { method: "POST" });
            const json = await res.json().catch(() => ({}));
            setTestResult(json as AmazonTestResult);
        } catch (err) {
            setTestResult({
                ok: false, status: null, latencyMs: 0,
                error: err instanceof Error ? err.message : String(err), marketplace: null,
            });
        } finally {
            setTesting(false);
        }
    };

    const syncCatalogue = async () => {
        if (readOnly) return;
        setSyncing(true);
        setError(null);
        try {
            const res = await apiFetch(`${base}/amazon/catalogue-sync`, { method: "POST" });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(json?.message ?? `HTTP ${res.status}`);
            if (json?.channel) onUpdated(json.channel);
            toast.success(t("amazon_catalogue_synced", {
                listings: String(json?.listings ?? 0),
                matched: String(json?.matched ?? 0),
            }));
            if (json?.fbaSkipped > 0) {
                toast.info(t("amazon_catalogue_fba_skipped", { n: String(json.fbaSkipped) }));
            }
            setSkuReload((n) => n + 1);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setSyncing(false);
        }
    };

    const pollNow = async () => {
        if (readOnly) return;
        setPolling(true);
        setError(null);
        try {
            const res = await apiFetch(`${base}/amazon/poll`, { method: "POST" });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(json?.message ?? `HTTP ${res.status}`);
            if (json?.channel) onUpdated(json.channel);
            toast.success(t("amazon_poll_result", {
                seen: String(json?.seen ?? 0),
                ingested: String(json?.ingested ?? 0),
                skipped: String(json?.skipped ?? 0),
            }));
            if (json?.failed > 0) {
                toast.error(t("amazon_poll_failed", { n: String(json.failed) }));
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setPolling(false);
        }
    };

    const doDelete = async () => {
        if (readOnly) return;
        setDeleting(true);
        setError(null);
        try {
            const res = await apiFetch(base, { method: "DELETE" });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(json?.message ?? `HTTP ${res.status}`);
            toast.success(t("amazon_channel_deleted", { name: channel.displayName }));
            onDeleted(channel.channelCode);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
            setConfirmDelete(false);
        } finally {
            setDeleting(false);
        }
    };

    const vendors = channel.vendors ?? [];
    const routed = vendors.some((v) => (v.fulfilmentWarehouses ?? []).length > 0);
    const credentialsReady = channel.outboundSecret.set
        && channel.inboundSecret.set
        && !!channel.amazonLwaClientId;
    const inputsDisabled = saving || deleting || readOnly;

    return (
        <details className="admin-integrations-card-details" id={channel.channelCode}>
            <summary className="admin-integrations-card-summary">
                <span className="admin-integrations-card-summary-title">{channel.displayName}</span>
                <span className="admin-integrations-card-summary-sub">
                    {channel.amazonSellerId} · {channel.amazonRegion?.toUpperCase()}
                </span>
                {channel.amazonUseSandbox && (
                    <span className="admin-integrations-pill admin-integrations-pill--dry-run">
                        {t("amazon_sandbox_pill")}
                    </span>
                )}
                {channel.enabled && channel.pushMode === "dry_run" && (
                    <span className="admin-integrations-pill admin-integrations-pill--dry-run">
                        {t("integrations_push_mode_dry_run")}
                    </span>
                )}
                <span
                    className={channel.enabled
                        ? "admin-integrations-status-pill admin-integrations-status-pill--ok"
                        : "admin-integrations-status-pill admin-integrations-status-pill--off"}
                >
                    {channel.enabled
                        ? t("integrations_status_pill_enabled")
                        : t("integrations_status_pill_disabled")}
                </span>
            </summary>

            <div className="admin-integrations-card-body">
                <p className="admin-integrations-card-hint">{t("amazon_channel_hint")}</p>

                {/* The single most consequential failure state: polling is
                    the ONLY way orders arrive, so a failing sweep means
                    silence, not an error anyone would notice. */}
                {channel.amazonOrdersPollError && (
                    <p className="admin-integrations-field-error">
                        <AlertTriangle size={14} />{" "}
                        {t("amazon_poll_error", { detail: channel.amazonOrdersPollError })}
                    </p>
                )}

                <div className="admin-integrations-form">
                    <div className="admin-integrations-form-row admin-integrations-form-row--toggle">
                        <label className="admin-settings-toggle">
                            <input
                                type="checkbox"
                                checked={channel.enabled}
                                onChange={() => toggle("enabled", !channel.enabled)}
                                disabled={inputsDisabled || (!channel.enabled && !credentialsReady)}
                            />
                            <span className="admin-settings-slider" />
                        </label>
                        <span className="admin-integrations-toggle-label">
                            {t("integrations_master_enabled")}
                        </span>
                        {saving && <Loader2 size={14} className="admin-integrations-spinner" />}
                    </div>
                    {!credentialsReady && (
                        <p className="admin-integrations-field-hint">{t("amazon_needs_credentials")}</p>
                    )}

                    {/* Sandbox is deliberately near the top: it changes
                        what every button below actually talks to, and a
                        card in sandbox mode that looked like any other
                        would be genuinely misleading. */}
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("amazon_sandbox")}</label>
                        <p className="admin-integrations-field-hint">{t("amazon_sandbox_hint")}</p>
                        <div className="admin-integrations-form-row admin-integrations-form-row--toggle">
                            <label className="admin-settings-toggle">
                                <input
                                    type="checkbox"
                                    checked={channel.amazonUseSandbox}
                                    onChange={() => toggle("amazonUseSandbox", !channel.amazonUseSandbox)}
                                    disabled={inputsDisabled}
                                />
                                <span className="admin-settings-slider" />
                            </label>
                            <span className="admin-integrations-toggle-label">
                                {channel.amazonUseSandbox
                                    ? t("amazon_sandbox_on")
                                    : t("amazon_sandbox_off")}
                            </span>
                        </div>
                    </div>

                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("integrations_push_mode")}</label>
                        <p className="admin-integrations-field-hint">{t("amazon_push_mode_hint")}</p>
                        <div className="admin-integrations-mode-toggle">
                            <button
                                type="button"
                                className={`admin-integrations-mode-btn${channel.pushMode === "dry_run" ? " admin-integrations-mode-btn--active" : ""}`}
                                onClick={() => { if (channel.pushMode !== "dry_run") patch({ pushMode: "dry_run" }).catch(() => {}); }}
                                disabled={inputsDisabled}
                            >
                                {t("integrations_push_mode_dry_run")}
                            </button>
                            <button
                                type="button"
                                className={`admin-integrations-mode-btn admin-integrations-mode-btn--live${channel.pushMode === "live" ? " admin-integrations-mode-btn--active" : ""}`}
                                onClick={() => { if (channel.pushMode !== "live") patch({ pushMode: "live" }).catch(() => {}); }}
                                disabled={inputsDisabled}
                            >
                                {t("integrations_push_mode_live")}
                            </button>
                        </div>
                    </div>

                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("amazon_field_display_name")}</label>
                        <input
                            type="text"
                            className="admin-integrations-input"
                            value={displayNameDraft}
                            onChange={(e) => setDisplayNameDraft(e.target.value)}
                            onBlur={() => {
                                const v = displayNameDraft.trim();
                                if (v && v !== channel.displayName) patch({ displayName: v }).catch(() => {});
                            }}
                            disabled={inputsDisabled}
                        />
                    </div>

                    <div className="admin-integrations-status-grid">
                        <div className="admin-integrations-status-cell">
                            <span className="admin-integrations-status-label">{t("amazon_field_seller_id")}</span>
                            <span className="admin-integrations-status-value">{channel.amazonSellerId}</span>
                        </div>
                        <div className="admin-integrations-status-cell">
                            <span className="admin-integrations-status-label">{t("amazon_field_marketplace")}</span>
                            <span className="admin-integrations-status-value">{channel.amazonMarketplaceId}</span>
                        </div>
                        <div className="admin-integrations-status-cell">
                            <span className="admin-integrations-status-label">{t("amazon_field_region")}</span>
                            <span className="admin-integrations-status-value">{channel.amazonRegion?.toUpperCase()}</span>
                        </div>
                    </div>

                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("amazon_field_client_id")}</label>
                        <p className="admin-integrations-field-hint">{t("amazon_field_client_id_hint")}</p>
                        <input
                            type="text"
                            className="admin-integrations-input"
                            value={clientIdDraft}
                            onChange={(e) => setClientIdDraft(e.target.value)}
                            onBlur={() => {
                                const v = clientIdDraft.trim();
                                if (v !== channel.amazonLwaClientId) patch({ amazonLwaClientId: v }).catch(() => {});
                            }}
                            placeholder="amzn1.application-oa2-client…"
                            disabled={inputsDisabled}
                        />
                    </div>

                    <SecretField
                        label={t("amazon_field_client_secret")}
                        hint={t("amazon_field_client_secret_hint")}
                        secret={channel.inboundSecret}
                        onSave={(v) => patch({ inboundSecret: v })}
                        onClear={() => patch({ inboundSecret: null })}
                        disabled={readOnly}
                    />

                    <SecretField
                        label={t("amazon_field_refresh_token")}
                        hint={t("amazon_field_refresh_token_hint")}
                        secret={channel.outboundSecret}
                        onSave={(v) => patch({ outboundSecret: v })}
                        onClear={() => patch({ outboundSecret: null })}
                        disabled={readOnly}
                    />

                    <div className="admin-integrations-test-row">
                        <button
                            type="button"
                            className="btn btn-sm"
                            onClick={runTest}
                            disabled={testing || !credentialsReady || readOnly}
                        >
                            {testing
                                ? <><Loader2 size={14} className="admin-integrations-spinner" /> {t("integrations_test_running")}</>
                                : <><Wifi size={14} /> {t("integrations_test_connection")}</>}
                        </button>
                        {testResult && (
                            <span
                                className={testResult.ok
                                    ? "admin-integrations-test-result admin-integrations-test-result--ok"
                                    : "admin-integrations-test-result admin-integrations-test-result--fail"}
                            >
                                {testResult.ok
                                    ? (testResult.sandbox
                                        ? t("amazon_test_success_sandbox", { latencyMs: String(testResult.latencyMs) })
                                        : t("amazon_test_success", {
                                            marketplace: testResult.marketplace?.name ?? channel.amazonMarketplaceId,
                                            latencyMs: String(testResult.latencyMs),
                                        }))
                                    : <><WifiOff size={14} /> {t("integrations_test_failure", { reason: testResult.error ?? "?" })}</>}
                            </span>
                        )}
                    </div>

                    {/* --- Consolidation ---------------------------------- */}
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">
                            {t("integrations_consolidate_orders")}
                        </label>
                        <p className="admin-integrations-field-hint">{t("integrations_consolidate_orders_hint")}</p>
                        <div className="admin-integrations-form-row admin-integrations-form-row--toggle">
                            <label className="admin-settings-toggle">
                                <input
                                    type="checkbox"
                                    checked={channel.consolidateOrders}
                                    onChange={() => toggle("consolidateOrders", !channel.consolidateOrders)}
                                    disabled={inputsDisabled}
                                />
                                <span className="admin-settings-slider" />
                            </label>
                            <span className="admin-integrations-toggle-label">
                                {channel.consolidateOrders
                                    ? t("integrations_consolidate_orders_on")
                                    : t("integrations_consolidate_orders_off")}
                            </span>
                        </div>
                        {channel.consolidateOrders && (
                            <>
                                <p className="admin-integrations-field-hint">
                                    {t("amazon_address_only_hint")}
                                </p>
                                <div className="admin-integrations-form-row admin-integrations-form-row--toggle">
                                    <label className="admin-settings-toggle">
                                        <input
                                            type="checkbox"
                                            checked={channel.consolidationMatchAddressOnly}
                                            onChange={() => toggle("consolidationMatchAddressOnly", !channel.consolidationMatchAddressOnly)}
                                            disabled={inputsDisabled}
                                        />
                                        <span className="admin-settings-slider" />
                                    </label>
                                    <span className="admin-integrations-toggle-label">
                                        {channel.consolidationMatchAddressOnly
                                            ? t("amazon_address_only_on")
                                            : t("amazon_address_only_off")}
                                    </span>
                                </div>
                            </>
                        )}
                    </div>

                    {/* --- Catalogue -------------------------------------- */}
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("amazon_catalogue")}</label>
                        <p className="admin-integrations-field-hint">{t("amazon_catalogue_hint")}</p>
                        <div className="admin-integrations-test-row">
                            <button
                                type="button"
                                className="btn btn-sm"
                                onClick={syncCatalogue}
                                disabled={syncing || !credentialsReady || readOnly}
                            >
                                {syncing
                                    ? <><Loader2 size={14} className="admin-integrations-spinner" /> {t("shopify_catalogue_syncing")}</>
                                    : <><RefreshCw size={14} /> {t("shopify_catalogue_sync")}</>}
                            </button>
                            <span className="admin-integrations-status-value">
                                {channel.catalogueSyncedAt
                                    ? t("shopify_catalogue_state", {
                                        n: String(channel.catalogueSkuCount ?? 0),
                                        when: fmtAbsolute(channel.catalogueSyncedAt),
                                    })
                                    : t("shopify_catalogue_never")}
                            </span>
                        </div>
                    </div>

                    {/* --- Orders ----------------------------------------- */}
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("amazon_orders")}</label>
                        <p className="admin-integrations-field-hint">{t("amazon_orders_hint")}</p>
                        <div className="admin-integrations-test-row">
                            <button
                                type="button"
                                className="btn btn-sm"
                                onClick={pollNow}
                                disabled={polling || !channel.enabled || readOnly}
                                title={t("amazon_poll_now_hint")}
                            >
                                {polling
                                    ? <><Loader2 size={14} className="admin-integrations-spinner" /> {t("amazon_polling")}</>
                                    : <><DownloadCloud size={14} /> {t("amazon_poll_now")}</>}
                            </button>
                            <span className="admin-integrations-status-value">
                                {channel.amazonOrdersPolledAt
                                    ? t("amazon_last_polled", { when: fmtAbsolute(channel.amazonOrdersPolledAt) })
                                    : t("amazon_never_polled")}
                            </span>
                        </div>
                    </div>

                    {error && <p className="admin-integrations-field-error">{error}</p>}
                </div>

                <SkuMapPanel channelCode={channel.channelCode} reloadToken={skuReload} />

                <details className="admin-integrations-vendors-details">
                    <summary className="admin-integrations-vendors-summary">
                        <span>{t("amazon_routing_heading")}</span>
                        {!routed && (
                            <span className="admin-integrations-pill admin-integrations-pill--dry-run">
                                {t("amazon_unrouted")}
                            </span>
                        )}
                    </summary>
                    <div className="admin-integrations-vendors-body">
                        <p className="admin-integrations-vendors-hint">{t("amazon_routing_hint")}</p>
                        <div className="admin-integrations-vendors-list">
                            {vendors.map((v) => (
                                <VendorSubAccountRow
                                    key={v.id}
                                    channelCode={channel.channelCode}
                                    vendor={v}
                                    warehouses={warehouses}
                                    onChannelUpdated={onUpdated}
                                    disabled={readOnly}
                                />
                            ))}
                        </div>
                    </div>
                </details>

                <div className="admin-integrations-test-row">
                    {!confirmDelete ? (
                        <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => setConfirmDelete(true)}
                            disabled={!channel.isRemovable || inputsDisabled}
                            title={t("shopify_delete_store_hint")}
                        >
                            <Trash2 size={14} /> {t("amazon_delete_channel")}
                        </button>
                    ) : (
                        <>
                            <span className="admin-integrations-secret-confirm-text">
                                {t("shopify_delete_store_confirm", { name: channel.displayName })}
                            </span>
                            <button type="button" className="btn btn-sm btn-danger" onClick={doDelete} disabled={deleting || readOnly}>
                                {deleting
                                    ? <Loader2 size={14} className="admin-integrations-spinner" />
                                    : t("amazon_delete_channel")}
                            </button>
                            <button type="button" className="btn btn-sm" onClick={() => setConfirmDelete(false)} disabled={deleting}>
                                {t("cancel")}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </details>
    );
};

export default AmazonChannelCard;
